import React, { useState } from 'react';
import { ModernButton } from '../ui/ModernButton';
import { ArrowLeftIcon } from '../ui/Icons';

export function AddApplicantPage({ campaign, onBack, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        status: 'Unbearbeitet',
        notes: ''
    });

    const [errors, setErrors] = useState({});

    const statusOptions = ['Unbearbeitet', 'In Kontakt', 'Absage', 'Zusage'];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name ist erforderlich';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'E-Mail ist erforderlich';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Gültige E-Mail-Adresse erforderlich';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const newApplicant = {
            id: `applicant_${Date.now()}`,
            leadID: `lead_${Date.now()}`,
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            status: formData.status,
            experience: '', // Will be filled from questionnaire responses
            skills: '', // Will be filled from questionnaire responses
            notes: formData.notes.trim(),
            createdAt: new Date().toISOString(),
            lastChange: new Date().toISOString(),
            rawFunnelData: [] // Will be populated when questionnaire is completed
        };

        onSave(newApplicant);
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <ModernButton
                                onClick={onBack}
                                variant="secondary"
                                size="sm"
                                icon={<ArrowLeftIcon className="w-4 h-4" />}
                            >
                                Zurück
                            </ModernButton>
                            <div className="flex items-center space-x-3">
                                <div className={`w-4 h-4 rounded-full ${campaign?.color || 'bg-blue-500'} shadow-xs`}></div>
                                <div>
                                    <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                                        Neuen Bewerber hinzufügen
                                    </h1>
                                    <p className="text-slate-600 dark:text-slate-400">
                                        {campaign?.name || 'Kampagne'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                    <form onSubmit={handleSubmit} className="p-8 space-y-6">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                                Grunddaten
                            </h3>
                            
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                                        errors.name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                    }`}
                                    placeholder="Max Mustermann"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    E-Mail *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                                        errors.email ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                    }`}
                                    placeholder="max.mustermann@example.com"
                                />
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                )}
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Telefon
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="+49 123 456789"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Status
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                    {statusOptions.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>


                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Notizen
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                rows="4"
                                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                placeholder="Zusätzliche Notizen zum Bewerber..."
                            />
                        </div>

                        {/* Form Actions */}
                        <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200 dark:border-slate-700">
                            <ModernButton
                                type="button"
                                onClick={onBack}
                                variant="secondary"
                            >
                                Abbrechen
                            </ModernButton>
                            <ModernButton
                                type="submit"
                                variant="primary"
                            >
                                Bewerber hinzufügen
                            </ModernButton>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
